import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Login from './pages/Login';
import LoginOTP from './pages/LoginOTP';
import VerifyEmail from './pages/VerifyEmail';
import SelectCampus from './pages/SelectCampus';
import SwitchCampus from './pages/SwitchCampus';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Home from './pages/Home';
import CourseList from './pages/Courses/List';
import CourseDetail from './pages/Courses/Detail';
import AssessmentList from './pages/Assessments/List';
import AssessmentTake from './pages/Assessments/Take';
import AssessmentResult from './pages/Assessments/Result';
import Submissions from './pages/Assessments/Submissions';
import ManualGrading from './pages/Admin/ManualGrading';
import EnrollmentList from './pages/Enrollments/List';
import EnrollmentDetail from './pages/Enrollments/Detail';
import AdminEnrollments from './pages/Admin/Enrollments';
import PaymentVerification from './pages/Admin/PaymentVerification';
import PendingStudents from './pages/Admin/PendingStudents';
import AdminCampuses from './pages/Admin/Campuses';
import AdminCohorts from './pages/Admin/Cohorts';
import AdminNotifications from './pages/Admin/Notifications';
import AdminStudents from './pages/Admin/Students';
import AdminStudentDetail from './pages/Admin/StudentDetail';
import AdminCourses from './pages/Admin/Courses';
import AdminCourseForm from './pages/Admin/CourseForm';
import CourseContent from './pages/Admin/CourseContent';
import AdminAssessments from './pages/Admin/AdminAssessments';
import AdminAssessmentDetail from './pages/Admin/AdminAssessmentDetail';
import AdminAssessmentQuestions from './pages/Admin/AdminAssessmentQuestions';
import Profile from './pages/Profile';
import TermsOfUse from './pages/Legal/TermsOfUse';
import PrivacyPolicy from './pages/Legal/PrivacyPolicy';
import ProtectedRoute from './routes/ProtectedRoute';
import RoleProtectedRoute from './routes/RoleProtectedRoute';
import About from './pages/About';
import Contact from './pages/Contact';
import Analytics from './pages/Analytics';
import ReportingCenter from './pages/Admin/ReportingCenter';
import NotificationInbox from './pages/Notifications/Inbox';
import HelpCenter from './pages/HelpCenter';
import { ThemeProvider } from './context/ThemeContext';
import CognitoSessionBridge from './components/CognitoSessionBridge';

function App() {
  return (
    <ThemeProvider>
      <div className="app">
      <Toaster position="top-right" />
      <CognitoSessionBridge />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/auth/login" element={<Login />} />
        <Route path="/auth/login-otp" element={<LoginOTP />} />
        <Route path="/auth/verify-email" element={<VerifyEmail />} />
        <Route path="/auth/select-campus" element={<SelectCampus />} />
        <Route path="/auth/register" element={<Register />} />

        {/* Legacy redirects */}
        <Route path="/login" element={<Navigate to="/auth/login" replace />} />
        <Route path="/register" element={<Navigate to="/auth/register" replace />} />

        <Route path="/courses" element={<CourseList />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />

        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/assessments"
          element={
            <ProtectedRoute>
              <AssessmentList />
            </ProtectedRoute>
          }
        />

        <Route
          path="/courses/:id"
          element={
            <ProtectedRoute>
              <CourseDetail />
            </ProtectedRoute>
          }
        />

        <Route
          path="/assessments/:id/take"
          element={
            <ProtectedRoute>
              <AssessmentTake />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/enrollments"
          element={
            <RoleProtectedRoute allowedRoles={['admin', 'campus_admin', 'superuser', 'finance']}>
              <AdminEnrollments />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/admin/enrollments/payment-verification"
          element={
            <RoleProtectedRoute allowedRoles={['finance', 'admin', 'campus_admin', 'superuser']}>
              <PaymentVerification />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/admin/pending-students"
          element={
            <RoleProtectedRoute>
              <PendingStudents />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/admin/campuses"
          element={
            <RoleProtectedRoute>
              <AdminCampuses />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/admin/cohorts"
          element={
            <RoleProtectedRoute>
              <AdminCohorts />
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <NotificationInbox />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/switch-campus"
          element={
            <ProtectedRoute>
              <SwitchCampus />
            </ProtectedRoute>
          }
        />

        {/* Enrollment pages (student-facing) */}
        <Route
          path="/enrollments"
          element={
            <ProtectedRoute>
              <EnrollmentList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/enrollments/:id"
          element={
            <ProtectedRoute>
              <EnrollmentDetail />
            </ProtectedRoute>
          }
        />

        {/* Assessment result */}
        <Route
          path="/assessments/:id/result"
          element={
            <ProtectedRoute>
              <AssessmentResult />
            </ProtectedRoute>
          }
        />

        {/* Assessment Submissions */}
        <Route
          path="/assessments/submissions"
          element={
            <ProtectedRoute>
              <Submissions />
            </ProtectedRoute>
          }
        />

        {/* Admin: Manual Grading */}
        <Route
          path="/admin/submissions/:submissionId/grade"
          element={
            <RoleProtectedRoute>
              <ManualGrading />
            </RoleProtectedRoute>
          }
        />

        {/* Admin: Notifications */}
        <Route
          path="/admin/notifications"
          element={
            <RoleProtectedRoute>
              <AdminNotifications />
            </RoleProtectedRoute>
          }
        />

        {/* Admin: Course Management */}
        <Route
          path="/admin/courses"
          element={
            <RoleProtectedRoute>
              <AdminCourses />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/admin/courses/new"
          element={
            <RoleProtectedRoute>
              <AdminCourseForm />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/admin/courses/:id/edit"
          element={
            <RoleProtectedRoute>
              <AdminCourseForm />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/admin/courses/:id/content"
          element={
            <RoleProtectedRoute>
              <CourseContent />
            </RoleProtectedRoute>
          }
        />

        {/* Admin: Assessment Management */}
        <Route
          path="/admin/assessments"
          element={
            <RoleProtectedRoute>
              <AdminAssessments />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/admin/assessments/new"
          element={
            <RoleProtectedRoute>
              <AdminAssessmentDetail />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/admin/assessments/:id"
          element={
            <RoleProtectedRoute>
              <AdminAssessmentDetail />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/admin/assessments/:id/questions"
          element={
            <RoleProtectedRoute>
              <AdminAssessmentQuestions />
            </RoleProtectedRoute>
          }
        />

        {/* Admin: Students */}
        <Route
          path="/admin/students"
          element={
            <RoleProtectedRoute>
              <AdminStudents />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/admin/students/:id"
          element={
            <RoleProtectedRoute>
              <AdminStudentDetail />
            </RoleProtectedRoute>
          }
        />

        {/* Analytics - Student */}
        <Route
          path="/analytics"
          element={
            <ProtectedRoute>
              <Analytics />
            </ProtectedRoute>
          }
        />

        {/* Analytics - Admin Reporting Center */}
        <Route
          path="/admin/reports"
          element={
            <RoleProtectedRoute>
              <ReportingCenter />
            </RoleProtectedRoute>
          }
        />

        {/* Help Center */}
        <Route
          path="/help"
          element={
            <ProtectedRoute>
              <HelpCenter />
            </ProtectedRoute>
          }
        />

        {/* Legal pages (public) */}
        <Route path="/terms-of-use" element={<TermsOfUse />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      </Routes>
      </div>
    </ThemeProvider>
  );
}

export default App;
